import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import GenericPicture from "@/components/generic-picture/generic-picture.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/shadcn/table.tsx"
import {
  getCurrentAnimalImage,
  getGenderIcon,
  type Animal,
} from "@/features/farm"
import { getAgeFromDateOfBirth } from "@/utils/helpers/time-utils.ts"

interface AnimalTableProps {
  animals: Animal[]
  onRowClick: (animalId: string) => void
}

const AnimalTable: React.FC<AnimalTableProps> = ({ animals, onRowClick }) => {
  const { t } = useTranslation()
  const [sorting, setSorting] = React.useState<SortingState>([])

  const columns = useMemo<ColumnDef<Animal>[]>(
    () => [
      {
        accessorKey: "image",
        header: undefined,
        cell: ({ row }) => (
          <div className="w-[50px] shrink-0">
            <GenericPicture
              src={getCurrentAnimalImage(row.original)}
              alt={row.original.name || row.original.ain}
              size={50}
              borderRadius={8}
            />
          </div>
        ),
        enableSorting: false,
        width: 70,
      },
      {
        accessorKey: "name",
        header: t("name"),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        accessorKey: "ain",
        header: t("crotal"),
        cell: ({ row }) => (
          <span className="text-gray-600">{row.original.ain}</span>
        ),
      },
      {
        accessorKey: "birth_date",
        header: t("age"),
        cell: ({ row }) => (
          <span className="text-gray-600">
            {getAgeFromDateOfBirth(row.original.birth_date)}
          </span>
        ),
        sortingFn: (rowA, rowB) => {
          const dateA = new Date(rowA.original.birth_date).getTime()
          const dateB = new Date(rowB.original.birth_date).getTime()
          return dateA - dateB
        },
      },
      {
        accessorKey: "gender",
        header: t("sex"),
        cell: ({ row }) => getGenderIcon(row.original.gender),
        enableSorting: false,
      },
      {
        accessorKey: "statuses",
        header: t("status"),
        cell: ({ row }) => (
          <span className="text-gray-600">
            {row.original.statuses?.map((status) => t(status)).join(", ")}
          </span>
        ),
        sortingFn: (rowA, rowB) => {
          const statusA = rowA.original.statuses?.[0] || ""
          const statusB = rowB.original.statuses?.[0] || ""
          return statusA.localeCompare(statusB)
        },
      },
    ],
    [t],
  )

  const table = useReactTable({
    data: animals,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={
                    header.column.getCanSort()
                      ? "cursor-pointer select-none hover:bg-gray-50"
                      : ""
                  }
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex items-center gap-2">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getIsSorted() && (
                      <span>
                        {header.column.getIsSorted() === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => onRowClick(row.original.id)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default AnimalTable
